import React, {FC} from 'react';
import {useVideoPlayer} from '@dazn/lp-web/src/hooks/useVideoPlayer';
import styles from '@dazn/lp-web/src/components/VideoPlayer/Player/Player.module.css';
import {encodeURL} from '@dazn/lp-web/src/utils';
import {
    IBadgeTextBoxProps,
    IPlayArrowProps,
    IVideoFullScreenIconProps,
    IVideoThumbnailProps,
    TVideoPlayerProps
} from '@dazn/lp-web/src/types';
import 'plyr/dist/plyr.css';
import '@dazn/lp-web/src/components/VideoPlayer/Player/player-override.css';
import {FullScreenIcon} from '@dazn/lp-web/src/components/Icons/FullScreenIcon';
import {isPurchased} from '@dazn/lp-web/src/components/VideoPlayer/video-player.utils';

const PlayArrow: FC<IPlayArrowProps> = ({className = 'PlayArrow'}) => (
    <div className={`${styles.arrow} ${className}`} />
);

const BadgeTextBox: React.FC<IBadgeTextBoxProps> = ({badgeText}) => {
    return (
        <div className={styles.badgeTextBox}>
            <span>{badgeText}</span>
        </div>
    );
};

const VideoThumbnail: React.FC<IVideoThumbnailProps> = ({
    thumbnail,
    togglePlayerState
}) => {
    return (
        <div
            className={styles.videoThumbnail}
            style={{
                backgroundImage: `url(${thumbnail ? encodeURL(thumbnail) : ''})`
            }}
            onClick={togglePlayerState}
        >
            <PlayArrow />
        </div>
    );
};

const VideoFullScreenIcon: React.FC<IVideoFullScreenIconProps> = ({
    toggleFullScreen
}) => {
    return (
        <div
            className={styles.videoFullScreenIconBox}
            onClick={toggleFullScreen}
        >
            <FullScreenIcon size={20} fill="#f9fafa" />
        </div>
    );
};

const Player: React.FC<TVideoPlayerProps> = ({
    videoUrl,
    index,
    customPlayerId,
    thumbnail = '',
    badgeText = '',
    entitlementSetId
}) => {
    const {
        shouldShowThumbnail = false,
        playerId,
        videoContainerRef,
        toggleFullScreen,
        togglePlayerState
    } = useVideoPlayer(videoUrl, index, customPlayerId);

    const getRenderConditions = () => {
        return {
            showThumbnail: shouldShowThumbnail,
            showBadge: isPurchased(entitlementSetId || '')
        };
    };

    const {showThumbnail, showBadge} = getRenderConditions();

    return (
        <div className={`${styles.videoContainer}`} ref={videoContainerRef}>
            {showThumbnail && (
                <VideoThumbnail
                    thumbnail={thumbnail}
                    togglePlayerState={togglePlayerState}
                />
            )}
            {showBadge && <BadgeTextBox badgeText={badgeText} />}
            <VideoFullScreenIcon toggleFullScreen={toggleFullScreen} />
            <div
                id={playerId}
                data-plyr-provider={videoUrl.provider}
                data-plyr-embed-id={encodeURL(videoUrl.src)}
                onTouchStart={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                onTouchMove={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
            />
        </div>
    );
};

export default Player;
